import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

const CONTEXT_ID = 'TraceDockDataLayerContext';
const INITIALIZED_KEY = Symbol.for('mezaldy.tracedock.data-layer.initialized');

let context;
let initialEventsDispatched = false;
let consentCollectedDuringPage = false;

function parseContext(source) {
  const element = source.getElementById(CONTEXT_ID);
  if (!(element instanceof HTMLScriptElement)) return null;

  try {
    return JSON.parse(element.textContent || 'null');
  } catch {
    return null;
  }
}

function getContext() {
  if (context !== undefined) {
    return context;
  }

  context = parseContext(document);
  return context;
}

function marketingAllowed() {
  return window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
}

function createEventId() {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function compact(value) {
  if (Array.isArray(value)) return value.map(compact);

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entry]) => entry !== undefined && entry !== null && entry !== '')
        .map(([key, entry]) => [key, compact(entry)])
    );
  }

  return value;
}

function moneyFromCents(value) {
  const cents = Number(value);
  return Number.isFinite(cents) ? cents / 100 : undefined;
}

function shopifyGid(resource, id) {
  if (id === undefined || id === null || id === '') return undefined;

  const value = String(id);
  return value.startsWith('gid://') ? value : `gid://shopify/${resource}/${value}`;
}

function variantTitle(title) {
  return title && title !== 'Default Title' ? title : undefined;
}

function itemFromLine(line, quantity) {
  const productId = line?.product_id ?? line?.productId;
  const variantId = line?.variant_id ?? line?.variantId;
  const price = moneyFromCents(line?.final_price ?? line?.price);

  return compact({
    internal_id: productId,
    item_id: variantId ?? productId,
    item_name: line?.product_title ?? line?.productTitle ?? line?.title,
    sku: line?.sku,
    price,
    google_business_vertical: 'retail',
    item_category: line?.product_type ?? line?.productType,
    id: variantId ?? productId,
    item_brand: line?.vendor,
    variant: variantTitle(line?.variant_title ?? line?.variantTitle),
    item_variant: variantTitle(line?.variant_title ?? line?.variantTitle),
    quantity,
  });
}

function pushEvent(event, payload = {}) {
  if (!getContext() || !marketingAllowed()) return false;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(
    compact({
      event,
      timestamp: new Date().toISOString(),
      id: createEventId(),
      url: window.location.href,
      ...payload,
    })
  );

  return true;
}

function trackCommerceEvent(event, line, quantity, currency) {
  const normalizedQuantity = Number(quantity);
  if (!line || !Number.isFinite(normalizedQuantity) || normalizedQuantity <= 0) return;

  const item = itemFromLine(line, normalizedQuantity);
  const unitPrice = moneyFromCents(line.final_price ?? line.price);

  pushEvent(event, {
    currency,
    value: unitPrice === undefined ? undefined : Number((unitPrice * normalizedQuantity).toFixed(2)),
    quantity: normalizedQuantity,
    product_id: shopifyGid('Product', line.product_id ?? line.productId),
    product_title: line.product_title ?? line.productTitle ?? line.title,
    product_variant_title: variantTitle(line.variant_title ?? line.variantTitle),
    product_sku: line.sku,
    items: [item],
  });
}

function trackProductViewed(productContext) {
  if (!productContext?.product) return;

  const { product, variant } = productContext.product;
  const price = moneyFromCents(variant.price);
  const item = compact({
    internal_id: product.id,
    item_id: variant.id ?? product.id,
    item_name: product.title,
    sku: variant.sku,
    price,
    google_business_vertical: 'retail',
    item_category: product.type,
    id: variant.id ?? product.id,
    item_brand: product.vendor,
    variant: variantTitle(variant.title),
    item_variant: variantTitle(variant.title),
    quantity: 1,
  });

  pushEvent('product_viewed', {
    currency: productContext.currency,
    value: price,
    product_id: shopifyGid('Product', product.id),
    product_title: product.title,
    product_variant_title: variantTitle(variant.title),
    product_sku: variant.sku,
    items: [item],
  });
}

export function trackCartAdditions(cart, additions) {
  if (!cart || !Array.isArray(additions)) return;

  const currency = cart.currency ?? getContext()?.currency;

  for (const addition of additions) {
    const variantId = String(addition.variantId);
    const line = cart.items?.find((item) => String(item.variant_id) === variantId);
    trackCommerceEvent('product_added_to_cart', line, addition.quantity, currency);
  }
}

export function trackCartQuantityChange(line, previousQuantity, nextQuantity, currency) {
  const previous = Number(previousQuantity);
  const next = Number(nextQuantity);
  if (!Number.isFinite(previous) || !Number.isFinite(next) || previous === next) return;

  trackCommerceEvent(
    next > previous ? 'product_added_to_cart' : 'product_removed_from_cart',
    line,
    Math.abs(next - previous),
    currency
  );
}

function emitInitialEvents() {
  if (initialEventsDispatched || consentCollectedDuringPage || !marketingAllowed()) return;

  const pageContext = getContext();
  if (!pageContext) return;

  initialEventsDispatched = true;

  pushEvent('page_viewed', {
    page_location: window.location.href,
    page_title: document.title,
  });

  trackProductViewed(pageContext);

  if (pageContext.collection) {
    pushEvent('collection_viewed', {
      collection_id: shopifyGid('Collection', pageContext.collection.id),
      collection_title: pageContext.collection.title,
    });
  }

  if (pageContext.cart) {
    const items = pageContext.cart.items.map((line) => itemFromLine(line, line.quantity));
    pushEvent('cart_viewed', {
      cart_id: pageContext.cart.id,
      currency: pageContext.currency,
      value: moneyFromCents(pageContext.cart.total_price),
      quantity: pageContext.cart.item_count,
      items,
    });
  }
}

function initializeSearchTracking() {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const searchPath = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin).pathname;
      if (new URL(form.action, window.location.origin).pathname !== searchPath) return;

      const query = String(new FormData(form).get('q') || '').trim();
      if (query) pushEvent('search_submitted', { query });
    },
    { capture: true }
  );
}

function initializeProductTracking() {
  document.addEventListener(ThemeEvents.variantUpdate, (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;
    if (!(event.target instanceof HTMLElement) || event.target.dataset.templateProductMatch !== 'true') return;

    const updatedContext = parseContext(event.detail.data.html);
    trackProductViewed(updatedContext);
  });
}

function initializeConsent() {
  document.addEventListener('visitorConsentCollected', () => {
    consentCollectedDuringPage = true;
  });

  const customerPrivacy = window.Shopify?.customerPrivacy;
  if (customerPrivacy) {
    emitInitialEvents();
    return;
  }

  window.Shopify?.loadFeatures?.([
    {
      name: 'consent-tracking-api',
      version: '0.1',
      onLoad: emitInitialEvents,
    },
  ]);
}

function initializeTraceDockDataLayer() {
  if (window[INITIALIZED_KEY]) return;

  window[INITIALIZED_KEY] = true;
  initializeSearchTracking();
  initializeProductTracking();
  initializeConsent();
}

if (getContext()) {
  initializeTraceDockDataLayer();
}
